/**
 * Chi vede MaeSpace, e come ci si entra.
 *
 * ⚠️ Il cancello si legge **prima** di chiamare, non dopo. Il contratto F1 diceva «mostrate il
 * bottone sempre, ci pensano i codici d'errore»: era un bottone che rispondeva «non attivo per
 * questa azienda» solo al clic, e la regola 7 dice l'opposto. Dal BE v1.244.0 le tre condizioni si
 * sanno in anticipo:
 * 1. il **modulo** `maespace` acceso per l'azienda (`me.modules`, spento senza riga);
 * 2. il **claim** `maespace.sso` nel token;
 * 3. **non** essere in impersonificazione (`impersonated_by` nel token). Qui il comando non sparisce:
 *    si spegne con il motivo, perché chi impersona deve capire perché non va.
 * I 403 del server restano come rete, e `notifyError` li sa dire.
 */
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/providers/AuthProvider';
import { useAccountMe } from '@/features/auth/useAccountMe';
import { flagAcceso } from '@/shared/lib/tenantModules';
import { impersonatedBy } from '@/features/auth/impersonation';
import { CLAIM, hasClaim } from '@/shared/permissions/claims';
import { openExternalWindow } from '@/shared/lib/nativePlatform';
import { notifyError, notifySuccess, notifyWarn } from '@/shared/lib/notify';
import { maespaceKeys, requestSsoLink, syncProjectRoom, useRooms } from './api';

export const MAESPACE_FLAG = 'maespace.enabled';

export interface MaeSpaceAccess {
  /** Il modulo è acceso per l'azienda: la stanza della commessa si può mostrare. */
  moduleOn: boolean;
  /** «Apri in MaeSpace» si disegna (modulo acceso e permesso presente). */
  canOpen: boolean;
  /** Se c'è, il comando si disegna spento con questo motivo. */
  disabledReason: string | null;
}

/** La regola, pura: è quella che le prove esercitano. */
export function maespaceAccess(input: {
  moduleOn: boolean;
  claims: readonly string[];
  isSuperAdmin: boolean;
  impersonating: boolean;
}): MaeSpaceAccess {
  const allowed = input.isSuperAdmin || hasClaim(input.claims, CLAIM.maespaceSso);
  return {
    moduleOn: input.moduleOn,
    canOpen: input.moduleOn && allowed,
    disabledReason: input.impersonating
      ? 'Durante un’impersonificazione non si entra in MaeSpace al posto dell’utente.'
      : null,
  };
}

/**
 * ⚠️ Legge `me.modules` e il token, e **non** `useAccessContext`: quello si porta dietro la lettura di
 * `tenant-attributes` e la piattaforma, che qui non servono. MaeSpace è spento senza riga
 * (`OffWhenMissing`), quindi il ripiego su `tenant-attributes` non avrebbe niente da dire: un
 * `/account/me` che non ha ancora risposto, o un BE che non manda il modulo, vale «spento».
 */
export function useMaeSpaceAccess(): MaeSpaceAccess {
  const { decoded, claims, isAuthenticated } = useAuth();
  const me = useAccountMe({ enabled: Boolean(isAuthenticated) });
  return maespaceAccess({
    moduleOn: flagAcceso(me.data?.modules, MAESPACE_FLAG, () => false, false),
    claims: claims ?? decoded?.claims ?? [],
    isSuperAdmin: decoded?.superadmin === 'True',
    impersonating: Boolean(impersonatedBy(decoded)),
  });
}

/**
 * Apre MaeSpace già autenticati.
 *
 * ⚠️ Va chiamata **direttamente** dal gestore del clic: la finestra si apre prima della richiesta
 * (Safari blocca un `window.open` dopo un `await`), e riceve l'indirizzo quando il server l'ha dato.
 * Il link vale 60 secondi ed è monouso: per questo non si prepara mai in anticipo.
 */
export async function openMaeSpace(): Promise<void> {
  await openLinkFrom(async () => ({ url: (await requestSsoLink()).redirectUrl }), 'Apertura di MaeSpace…');
}

/**
 * Apre un indirizzo che arriva **solo dopo una chiamata** — il link SSO, un download firmato da 15
 * minuti. È la stessa coreografia per tutti: finestra aperta al clic, indirizzo assegnato quando
 * arriva, finestra chiusa e motivo detto se la chiamata fallisce. Va chiamata **direttamente** dal
 * gestore del clic, prima di qualunque `await`.
 */
export async function openLinkFrom(request: () => Promise<{ url: string }>, waitingText = 'Apertura in corso…'): Promise<boolean> {
  const target = openExternalWindow(waitingText);
  if (!target) {
    notifyWarn('Il browser ha bloccato la finestra: consenti le finestre a comparsa per questo sito e riprova.', 'MaeSpace');
    return false;
  }
  try {
    const { url } = await request();
    target.go(url);
    return true;
  } catch (e) {
    target.close();
    notifyError(e, 'MaeSpace');
    return false;
  }
}

/** La stessa azione, per chi la vuole stabile fra un render e l'altro. */
export function useOpenMaeSpace() {
  return useCallback(() => { void openMaeSpace(); }, []);
}

/** Le commesse (fra quelle passate) che hanno una stanza attiva. Vuoto e senza chiamate a modulo spento. */
export function useActiveRoomIds(projectIds: readonly string[]): ReadonlySet<string> {
  const { moduleOn } = useMaeSpaceAccess();
  const q = useRooms(projectIds, moduleOn);
  return useMemo(
    () => new Set((q.data ?? []).filter((r) => r.enabled && r.status === 'active').map((r) => r.projectId)),
    [q.data],
  );
}

/**
 * Crea (o riallinea) subito la stanza di un progetto, fuori coda. È lo stesso gesto per «Crea stanza
 * MaeSpace» e «Allinea ora»: il BE crea la stanza se non c'è, e la aggiorna se c'è.
 */
export function useProjectRoomSync(projectId: string) {
  const qc = useQueryClient();
  const [running, setRunning] = useState(false);
  const run = useCallback(async (creating: boolean) => {
    setRunning(true);
    try {
      const r = await syncProjectRoom(projectId);
      if (r.outcome === 'skipped') notifyWarn(r.detail ?? 'La stanza non è stata creata.', 'MaeSpace');
      else if (r.outcome === 'created') notifySuccess('Stanza MaeSpace creata: il PM e il team attivo ne sono membri.', 'MaeSpace');
      else notifySuccess(r.outcome === 'unchanged' ? 'La stanza era già allineata.' : creating ? 'Stanza MaeSpace pronta.' : 'Stanza allineata.', 'MaeSpace');
      await Promise.all([
        qc.invalidateQueries({ queryKey: maespaceKeys.room(projectId) }),
        qc.invalidateQueries({ queryKey: ['maespace', 'rooms'] }),
        qc.invalidateQueries({ queryKey: ['maespace', 'my-rooms'] }),
      ]);
    } catch (e) {
      notifyError(e, 'MaeSpace');
    } finally {
      setRunning(false);
    }
  }, [projectId, qc]);
  return { run, running };
}
